import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { SCHEDULE_SLOT_LABELS } from './constants';
import { getSlotChipSx as slotChipSx } from './calendarChipColors';
import { generateMockLiveInfoForMonth } from './storage';

const CAL_HEADER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function monthGrid(monthCursor) {
  const start = monthCursor.startOf('month');
  const daysInMonth = start.daysInMonth();
  const leadingEmpty = start.day();
  const cells = [];
  for (let i = 0; i < leadingEmpty; i += 1) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(start.date(d));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

/**
 * @param {{
 *   value?: Record<string, { working?: boolean, slots?: string[] }>,
 *   onDayClick?: (d: import('dayjs').Dayjs) => void,
 *   toolbarActions?: React.ReactNode,
 * }} props
 */
function LiveCalendar({ value = {}, onDayClick, toolbarActions = null }) {
  const theme = useTheme();
  const [cursor, setCursor] = useState(() => dayjs().startOf('month'));

  const liveInfo = useMemo(() => {
    const mock = generateMockLiveInfoForMonth(cursor);
    const merged = { ...value };
    Object.keys(mock).forEach(k => {
      if (merged[k] == null) {
        merged[k] = mock[k];
      }
    });
    return merged;
  }, [value, cursor]);

  const cellItems = useMemo(() => {
    const raw = monthGrid(cursor);
    const monthLabel = cursor.format('YYYY-MM');
    return raw.map((cell, pos) => {
      const row = Math.floor(pos / 7);
      const col = pos % 7;
      return {
        cell,
        gridKey: cell
          ? cell.format('YYYY-MM-DD')
          : `placeholder-${monthLabel}-r${row}-c${col}`,
      };
    });
  }, [cursor]);

  const hidePrev = !cursor.startOf('month').isAfter(dayjs(), 'month');

  const goPrev = () => {
    setCursor(c => c.subtract(1, 'month').startOf('month'));
  };

  const goNext = () => {
    setCursor(c => c.add(1, 'month').startOf('month'));
  };

  const today = dayjs().startOf('day');

  return (
    <Box>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}>
          <Box
            sx={{
              flex: '1 1 50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: 0,
            }}>
            <Typography variant="h6" component="h2">
              Live Calendar
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              flex: '1 1 50%',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              minWidth: 0,
            }}>
            {toolbarActions}
          </Stack>
        </Box>
      </Paper>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
          }}>
          {!hidePrev ? (
            <IconButton
              aria-label="Previous month"
              onClick={goPrev}
              size="small">
              <ChevronLeft />
            </IconButton>
          ) : (
            <Box sx={{ width: 40, flexShrink: 0 }} />
          )}
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {cursor.format('MMM YYYY')}
          </Typography>
          <IconButton aria-label="Next month" onClick={goNext} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
      </Paper>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
          }}>
          {CAL_HEADER.map(h => (
            <Typography
              key={h}
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 0.5 }}>
              {h}
            </Typography>
          ))}
          {cellItems.map(({ cell, gridKey }) => {
            if (!cell) {
              return (
                <Box
                  key={gridKey}
                  sx={{
                    minHeight: 120,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
              );
            }
            const dayData = liveInfo[gridKey];
            const working = Boolean(dayData?.working);
            const slots = Array.isArray(dayData?.slots) ? dayData.slots : [];
            const isToday = cell.isSame(today, 'day');
            const isPast = cell.isBefore(today, 'day');
            const isClickable = Boolean(onDayClick) && !isPast;

            return (
              <Box
                key={gridKey}
                {...(isClickable
                  ? {
                      onClick: () => onDayClick?.(cell),
                      role: 'button',
                      tabIndex: 0,
                      onKeyDown: e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onDayClick?.(cell);
                        }
                      },
                    }
                  : {})}
                sx={{
                  minHeight: 120,
                  p: 0.75,
                  borderRadius: 1,
                  cursor: isClickable ? 'pointer' : 'default',
                  bgcolor: 'transparent',
                  border: isToday
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  ...(isClickable
                    ? {
                        '&:hover': {
                          outline: `1px solid ${theme.palette.divider}`,
                        },
                      }
                    : {}),
                }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    alignSelf: 'flex-start',
                    mb: 2,
                  }}>
                  {cell.format('ddd')} {cell.format('D')}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    gap: 0.7,
                    mt: 0.5,
                    minHeight: 0,
                    px: 0.5,
                  }}>
                  {working &&
                    slots.map(slotKey => (
                      <Chip
                        key={slotKey}
                        label={
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              color: 'inherit',
                            }}>
                            {SCHEDULE_SLOT_LABELS[slotKey] || slotKey}
                          </Typography>
                        }
                        size="small"
                        variant="filled"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          py: 0.25,
                          px: 0.5,
                          justifyContent: 'flex-start',
                          cursor: 'inherit',
                          ...slotChipSx(slotKey),
                        }}
                      />
                    ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}

export default LiveCalendar;
